/**
 * SECONDARY-TRIGGER.MODAL
 * Choose when a new secondary question is asked, before adding it
 */
import { useState } from "react"
import { withTranslation, WithTranslation } from "react-i18next"
import Modal from "@/components/modal"
import RadioOption from "@/components/radio-option"
import { Question } from "@/redux/question.types"

interface OwnProps {
  initialTrigger?: string | null //Trigger of the question the user clicked + on (same one by default)
  language: string
  onClose: Function
  onValidate: Function //({ triggerValue, triggerChoice }, dontAskAgain)
  primary: Question //Primary question : a single choice (QCM) or a scale
}

type Props = WithTranslation & OwnProps

const ALWAYS = "always"

function SecondaryTriggerModal(props: Props) {
  const { t } = props
  const isChoice = props.primary.type === "choice"
  const propositions = props.primary.Propositions.filter((x) => x.name[props.language])

  //Selected : "<" / ">" (scale), a proposition id or ALWAYS (QCM)
  const defaultTrigger = isChoice ? (propositions[0] ? propositions[0].id : ALWAYS) : "<"
  const isInitialValid =
    props.initialTrigger &&
    (isChoice
      ? props.initialTrigger === ALWAYS || propositions.some((x) => x.id === props.initialTrigger)
      : ["<", ">"].includes(props.initialTrigger))
  const [selected, setSelected] = useState<string>(isInitialValid ? (props.initialTrigger as string) : defaultTrigger)
  const isAnswer = isChoice && selected !== ALWAYS

  function validate(dontAsk: boolean = false) {
    if (isChoice) {
      props.onValidate({ triggerValue: "<", triggerChoice: selected === ALWAYS ? null : selected }, dontAsk)
    } else {
      props.onValidate({ triggerValue: selected, triggerChoice: null }, dontAsk)
    }
  }

  return (
    <Modal
      isCloseButtonVisible
      title={t("secondary_trigger_title")}
      onClose={props.onClose}
      onNext={() => validate()}
      onNextCta={t("secondary_trigger_add")}
      footerLeft={
        <span
          className="grey-t cursor-pointer self-center text-sm underline"
          title={t("secondary_trigger_dont_ask_hint")}
          onClick={() => validate(true)}>
          {t("secondary_trigger_dont_ask")}
        </span>
      }>
      <div className="grey-t mb-2 text-sm">
        {t("secondary_trigger_depends", { question: props.primary.name[props.language] || t("question_new") })}
      </div>

      {isChoice ? (
        <div>
          {propositions.length > 0 ? (
            <div>
              <RadioOption
                isSelected={isAnswer}
                onClick={() => !isAnswer && setSelected(propositions[0].id)}
                title={t("secondary_trigger_answer")}
                hint={t("secondary_trigger_answer_hint")}
              />
              {propositions.map((proposition) => (
                <RadioOption
                  key={proposition.id}
                  indent
                  isSelected={selected === proposition.id}
                  onClick={() => setSelected(proposition.id)}
                  title={proposition.name[props.language]}
                />
              ))}
            </div>
          ) : (
            <div className="grey-t text-sm">{t("secondary_trigger_no_answer")}</div>
          )}
          <RadioOption
            isSelected={selected === ALWAYS}
            onClick={() => setSelected(ALWAYS)}
            title={t("secondary_trigger_always")}
            hint={t("secondary_trigger_always_hint")}
          />
        </div>
      ) : (
        <div>
          <RadioOption
            isSelected={selected === "<"}
            onClick={() => setSelected("<")}
            title={t("secondary_trigger_low")}
            hint={t("secondary_trigger_low_hint")}
          />
          <RadioOption
            isSelected={selected === ">"}
            onClick={() => setSelected(">")}
            title={t("secondary_trigger_high")}
            hint={t("secondary_trigger_high_hint")}
          />
        </div>
      )}
    </Modal>
  )
}

export default withTranslation()(SecondaryTriggerModal)
